import styled, { css } from 'vue-styled-components';
import COLORS from '@design/colors.js';
import RingLoader from '@components/Common/RingLoader';

import {
    ABSOLUTE_VERTICAL_CENTER,
    applyHoverAndPress,
    GRAY,
} from '@styles/common.styled';
import { KEY_CODE } from '@consts';
import { FLEX_COLUMN_DIV } from '@styles/common.styled.component';
import { isValueExist } from '../../utils/utils';

import EyeIcon from '@svgIcons/eye.svg';
import EyeStrikeIcon from '@svgIcons/eye_strike.svg';

export default {
    name: 'StatusInput',
    props: {
        value: {
            type: String,
            default: '',
        },
        errorText: {
            type: String,
            default: '',
        },
        mask: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        counterVisible: {
            type: Boolean,
            default: true,
        },
        type: {
            type: String,
            default: 'text',
        },
        maxLength: Number,
        autocorrect: {
            type: String,
            default: 'on',
        },
        autocapitalize: {
            type: String,
            default: 'none',
        },
        name: {
            type: String,
            default: '',
        },
        css: {
            type: Array,
            default() {
                return [];
            },
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        inputType: {
            type: String,
            default: 'input',
        },
        successText: String,
        cleaner: {
            type: Function,
            default: R.identity,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        dir: {
            type: String,
            default: 'ltr', // rtl
        },
        autoFocus: {
            type: Boolean,
            default: false,
        },
        errorElm: {
            type: undefined,
        },
        disableAutofill: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            focused: false,
            innerType: '',
            isReadonly: this.disableAutofill,
        };
    },
    created() {
        this.innerType = this.type;
    },
    mounted() {
        this.$nextTick(() => {
            if (this.autoFocus) {
                const { input = {} } = this.$refs;
                if (isValueExist(input)) {
                    input.focus();
                }
            }
        });
    },
    render() {
        const isError = this._status === 'ERROR';

        const bottomMargin =
            isError || this.counterVisible || !!this.successText;

        const Wrapper = this.container;

        return (
            <Wrapper>
                <TopContainer status={this._status} dir={this.dir}>
                    {this.$slots.icon}
                    {this.renderInput()}
                    {this.$slots.last}
                    {this.isLoading && <RingLoader />}
                    {this.eyeIconVisible &&
                        (this.innerType === 'password' ? (
                            <EyeStrikeIcon
                                v-view-box
                                class="eye-icon"
                                onClick={this.togglePasswordInnerType}
                            />
                        ) : (
                            <EyeIcon
                                v-view-box
                                class="eye-icon"
                                onClick={this.togglePasswordInnerType}
                            />
                        ))}
                </TopContainer>
                <BottomContainer
                    class="bottom-container"
                    hasTopMargin={bottomMargin}
                    dir={this.dir}
                >
                    {isError && this.errorText ? (
                        <BottomText domPropsInnerHTML={this.errorText} />
                    ) : (
                        <BottomText>{this.errorElm}</BottomText>
                    )}
                    {!isError && this.successText && (
                        <BottomText
                            domPropsInnerHTML={this.successText}
                            type="success"
                        />
                    )}
                    {this.counterVisible && (
                        <TextLength status={this._status}>
                            {this.length}
                        </TextLength>
                    )}
                </BottomContainer>
            </Wrapper>
        );
    },
    methods: {
        renderInput() {
            const props = {
                attrs: {
                    ...R.pick(
                        [
                            'placeholder',
                            'autocorrect',
                            'autocapitalize',
                            'disabled',
                            'value',
                            'name',
                        ],
                        this
                    ),
                    type: this.innerType,
                    ...(this.disableAutofill && {
                        autocomplete: this.name ? `section-${this.name} off` : 'off',
                        readonly: this.isReadonly,
                    }),
                },
                on: {
                    input: (e) => {
                        const value = e.target.value || '';
                        const cleaned = this.cleaner(value);
                        this.$emit('input', cleaned);

                        if (cleaned !== value) {
                            this.$forceUpdate();
                        }
                    },
                    mousedown: () => {
                        if (this.isReadonly) {
                            this.isReadonly = false;
                        }
                    },
                    focus: () => {
                        if (this.isReadonly) {
                            this.isReadonly = false;
                        }
                        this.focused = true;
                        this.$emit('focus');
                    },
                    blur: (e) => {
                        this.focused = false;
                        this.$emit('blur', e);
                    },
                    keypress: (e) => {
                        this.$emit('keypress', e);
                        if (e.keyCode === KEY_CODE.enter) {
                            this.$emit('enter', e);
                        }
                    },
                    keydown: (e) => {
                        this.$emit('keydown', e);
                        if (e.keyCode === KEY_CODE.up) {
                            this.$emit('up', e);
                        }
                        if (e.keyCode === KEY_CODE.down) {
                            this.$emit('down', e);
                        }
                    },
                    keyup: (e) => this.$emit('keyup', e),
                    compositionstart: (e) => this.$emit('compositionstart', e),
                    compositionend: (e) => this.$emit('compositionend', e),
                },
                directives: [
                    this.mask
                        ? {
                              name: 'mask',
                              value: this.mask,
                          }
                        : {},
                ],
                ref: 'input',
            };

            if (this.inputType === 'input') {
                return (
                    <input
                        {...props}
                        class="status-input status-input--input"
                        value={this.value}
                        maxLength={this.maxLength}
                    />
                );
            } else {
                return (
                    <textarea
                        {...props}
                        class="status-input status-input--textarea"
                        value={this.value}
                        maxLength={this.maxLength}
                    />
                );
            }
        },
        togglePasswordInnerType() {
            this.innerType = this.innerType === 'text' ? 'password' : 'text';
        },
    },
    computed: {
        length: R.path(['value', 'length']),
        _status() {
            if (this.errorText || this.errorElm) {
                return 'ERROR';
            } else if (this.focused) {
                return 'FOCUSED';
            } else if (this.length === 0) {
                return 'EMPTY';
            }

            return 'NORMAL';
        },
        container() {
            return styled(InputWrapper)`
                ${this.css};
            `;
        },
        eyeIconVisible: R.propEq('type', 'password'),
        computedType() {
            const { type, eyeIconVisible } = this;
            if (!eyeIconVisible) {
                return type;
            } else {
                return this.innerType;
            }
        },
    },
};

const _STATUS_MAP = {
    EMPTY: css`
        border-bottom: 1px solid ${COLORS['gray-50']};
        padding: 16px;
        &::placeholder {
            color: ${COLORS['gray-40']};
        }
    `,
    ERROR: css`
        border-bottom: 2px solid ${COLORS['red-60']};
        padding: 16px 16px 15px 16px;
    `,
    FOCUSED: css`
        border-bottom: 2px solid ${COLORS['blue-60']};
        padding: 16px 16px 15px 16px;
        &::placeholder {
            color: ${COLORS['gray-40']};
        }
    `,
    NORMAL: css`
        border-bottom: 1px solid ${COLORS['gray-50']};
        padding: 16px;
    `,
};

const InputWrapper = styled(FLEX_COLUMN_DIV)`
    position: relative;
`;

const BottomContainer = styled('div', { hasTopMargin: Boolean, dir: String })`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-top: ${({ hasTopMargin }) => (hasTopMargin ? '12px' : 0)};
    text-align: ${({ dir }) => (dir === 'ltr' ? 'left' : 'right')};
`;

const BottomText = styled('span', { type: String | undefined })`
    font-weight: 500;
    font-size: 14px;
    color: ${({ type }) =>
        type === 'success' ? COLORS['blue-60'] : COLORS['red-60']};
    white-space: pre-line;
    width: 100%;
`;

const TextLength = styled('span', { status: String })`
    font-size: 14px;
    font-weight: 500;
    color: ${({ status }) =>
        status === 'ERROR' ? COLORS['red-60'] : COLORS['gray-40']};
    display: flex;
    justify-content: flex-end;
    margin-bottom: 19px;
    margin-left: auto;
`;

const TopContainer = styled('div', { status: String, dir: String })`
    position: relative;
    width: 100%;
    display: flex;

    input,
    textarea {
        border: none;
        width: 100%;
        font-size: 16px;
        font-weight: 400;
        color: ${COLORS['gray-100']};
        border-top-left-radius: 2px;
        border-top-right-radius: 2px;
        background-color: ${COLORS['gray-10']};
        text-align: ${({ dir }) => (dir === 'ltr' ? 'start' : 'end')};

        &:focus,
        &:active {
            outline: none;
        }
        ${({ status }) => _STATUS_MAP[status]}
        @media screen and (max-width: 767px) {
            font-size: 14px !important;
        }
        &:disabled {
            background: ${COLORS['gray-20']};
            cursor: not-allowed;
        }
    }

    input {
        @media screen and (max-width: 767px) {
            height: 44px;
        }

        & + .lds-ring {
            ${ABSOLUTE_VERTICAL_CENTER};
            right: 8px;
        }
    }

    .eye-icon {
        position: absolute;
        width: 20px;
        height: 20px;
        right: 12px;
        ${ABSOLUTE_VERTICAL_CENTER};
        cursor: pointer;
        path {
            ${applyHoverAndPress(GRAY(50), 'fill')}
        }
    }
`;
